import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { Modal, Row, Col, ListGroup } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@store/index'
import ButtonGroup from '@components/common/ButtonGroup/ButtonGroup.component'
import ClaimMemoNote from './ClaimMemoNote'
import ClaimMemoAttachments from './ClaimMemoAttachments'
import { addOrUpdateAttachmentThunk } from '@store/actions/claimFeature.action'
import { fetchAllNotes } from '@store/actions/note.action'
import {
  Note,
  Attachment as ClaimAttachment,
  Claim,
} from '@dataTypes/claimFeature.types'
import { NoteContextType, type INote } from '@dataTypes/note.type'
import './ClaimMemoModal.css'
import { formatNoteUpdaterInitialLast } from '@utils/noteSidebarAuthor.util'
import { formatNoteTitleWithVersion } from '@utils/noteVersion.util'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import {
  getClaimIdForNotesHeader,
  getNotesClaimContextId,
} from '@utils/claims.util'
import { useNoteEditorOverlay } from '@components/common/NoteEditorOverlay'
import type { Patient } from '@dataTypes/PatientInfo.types'
import { fetchPatientById } from '@services/api/patient.api'
import { useAppSelector } from '@store/hooks'

type MemoAuthorFallback = {
  localUser: { first_name?: string; last_name?: string; username?: string }
  loggedInUser: {
    first_name?: string | null
    last_name?: string | null
  } | null
}

/** Map API/claim-level notes (INote[]) to memo Note[] for display in ClaimMemoNote */
function mapClaimNotesToMemoNotes(
  notes: INote[] | undefined,
  claim: Claim,
  authorFallback: MemoAuthorFallback,
): Note[] {
  if (!notes?.length) return []
  const pool = [...notes].sort((a, b) => {
    const aTime = new Date(
      a.date_time_updated_at ?? a.date_time_created_at ?? 0,
    ).getTime()
    const bTime = new Date(
      b.date_time_updated_at ?? b.date_time_created_at ?? 0,
    ).getTime()
    return bTime - aTime
  })
  const { localUser, loggedInUser } = authorFallback
  return pool.map((n) => {
    const displayTitle = formatNoteTitleWithVersion(
      {
        title: n.title ?? '',
        version: n.version,
        is_latest: n.is_latest,
        note_group_id: n.note_group_id,
        patient_id:
          n.patient_id ?? claim.patientIdSystem ?? claim.patientId ?? '',
        contact_id: n.contact_id ?? '',
        context_type: n.context_type ?? NoteContextType.CLAIMS,
        context_id:
          n.context_id ?? claim.claim_id ?? getNotesClaimContextId(claim) ?? '',
      },
      { versionChainPool: pool },
    )
    const displayAuthor =
      formatNoteUpdaterInitialLast(
        n.updatedBy?.first_name,
        n.updatedBy?.last_name,
      ) ||
      formatNoteUpdaterInitialLast(localUser.first_name, localUser.last_name) ||
      formatNoteUpdaterInitialLast(
        loggedInUser?.first_name,
        loggedInUser?.last_name,
      ) ||
      localUser.username ||
      undefined
    return {
      noteId: n.note_id,
      author:
        (n as INote & { createdBy?: { username?: string } }).createdBy
          ?.username ??
        localUser.username ??
        'Unknown',
      content: n.body ?? n.title ?? '',
      date:
        n.date_time_updated_at ??
        n.date_time_created_at ??
        new Date().toISOString(),
      displayTitle,
      displayAuthor,
    }
  })
}

interface ClaimMemoModalProps {
  isVisible: boolean
  closeModal: () => void
  /**
   * When the user opens a claim note in the full-screen note overlay, the memo modal closes
   * first. This callback re-opens the memo when that overlay is dismissed, so the user returns
   * to the same memo flow.
   */
  onRestoreAfterNoteOverlay?: () => void
  claimId: string
  readOnly?: boolean
  /** When provided (e.g. from list view), use this claim for display so notes/attachments match the same data source as edit mode. */
  claim?: Claim
}

const ClaimMemoModal: React.FC<ClaimMemoModalProps> = ({
  isVisible,
  closeModal,
  onRestoreAfterNoteOverlay,
  claimId,
  readOnly = false,
  claim: claimProp,
}) => {
  const dispatch = useDispatch<AppDispatch>()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const claimFromState = useSelector((state: RootState) =>
    state.claimFeature.claims.find((c) => c.id === claimId),
  )
  /** Use passed-in claim when provided (e.g. API list) so memo shows same notes as the viewed claim. */
  const claim = claimProp ?? claimFromState

  const selectedPatient = useAppSelector(
    (state) => state.patient.selectedPatient,
  )
  const loggedInUser = useAppSelector(
    (state) => state.chatInternal.loggedInUser,
  )
  const localStorageUser = useMemo<MemoAuthorFallback['localUser']>(() => {
    const fallbackUser: MemoAuthorFallback['localUser'] = {}

    try {
      const rawUser = localStorage.getItem('user')
      if (!rawUser) return fallbackUser
      const parsedUser = JSON.parse(rawUser) as unknown
      if (!parsedUser || typeof parsedUser !== 'object') return fallbackUser

      return parsedUser as MemoAuthorFallback['localUser']
    } catch {
      return fallbackUser
    }
  }, [])
  const { openNoteEditor, updateNoteEditor, closeNoteEditor } =
    useNoteEditorOverlay()

  /** Session id: ignore late patient-fetch results after close or a newer open. */
  const claimMemoPatientFetchSessionRef = useRef(0)
  /** Set when the memo is closed to show the global note overlay; used to re-open the memo on overlay close. */
  const shouldRestoreMemoAfterNoteOverlayRef = useRef(false)

  const [activeTab, setActiveTab] = useState<string>('notes')
  const [selectedAttachmentId, setSelectedAttachmentId] = useState<
    string | null
  >(null)
  /** Fetched claim-level notes when the claim from list/API has no notes embedded (same source as edit mode). */
  const [fetchedClaimNotes, setFetchedClaimNotes] = useState<INote[] | null>(
    null,
  )
  const [isFetchingClaimNotes, setIsFetchingClaimNotes] =
    useState<boolean>(false)
  const claimNotesContextId =
    claim != null ? getNotesClaimContextId(claim) : null

  useEffect(() => {
    if (claim?.attachments && claim.attachments.length > 0) {
      setSelectedAttachmentId(claim.attachments[0].itemNo)
    }
  }, [claim])

  useEffect(() => {
    if (!isVisible || !claim) {
      setFetchedClaimNotes(null)
      setIsFetchingClaimNotes(false)
      return
    }
    const contextId = claimNotesContextId
    if (!contextId) {
      setFetchedClaimNotes([])
      setIsFetchingClaimNotes(false)
      return
    }
    setIsFetchingClaimNotes(true)
    const loadingContainer = 'body'
    showLoading({
      container: loadingContainer,
    })
    dispatch(
      fetchAllNotes({
        contextType: 'claims',
        contextId,
      }),
    )
      .unwrap()
      .then((notes: INote[]) => {
        setFetchedClaimNotes(notes ?? [])
      })
      .catch(() => {
        setFetchedClaimNotes([])
      })
      .finally(() => {
        setIsFetchingClaimNotes(false)
        hideLoading(loadingContainer)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- id/claim_id identify claim; full `claim` omitted to avoid refetch on unrelated object churn
  }, [isVisible, claimNotesContextId, dispatch, hideLoading, showLoading])

  const handleUpdateNotes = async (updatedNotes: Note[]): Promise<void> => {
    if (readOnly) return
    const claimToUpdate = claimFromState ?? claim
    if (selectedAttachmentId && claimToUpdate) {
      const updatedAttachments = claimToUpdate.attachments.map((attachment) =>
        attachment.itemNo === selectedAttachmentId
          ? { ...attachment, notes: updatedNotes }
          : attachment,
      )

      if (claimFromState) {
        await dispatch(
          addOrUpdateAttachmentThunk({
            claimId,
            attachments: updatedAttachments,
          }),
        )
      }
    }
  }

  const handleUpdateAttachments = async (
    updatedAttachments: ClaimAttachment[],
  ): Promise<void> => {
    if (readOnly) return
    if (claimFromState) {
      await dispatch(
        addOrUpdateAttachmentThunk({
          claimId,
          attachments: updatedAttachments,
        }),
      )
    }
  }

  const handleSelectAttachment = (attachmentId: string): void => {
    setSelectedAttachmentId(attachmentId)
    setActiveTab('notes')
  }

  const handleClaimMemoNoteMutationSuccess = useCallback((): void => {
    if (!isVisible || !claim) return
    const contextId = getNotesClaimContextId(claim)
    if (!contextId) return
    void dispatch(
      fetchAllNotes({
        contextType: 'claims',
        contextId,
      }),
    )
      .unwrap()
      .then((notes: INote[]) => {
        setFetchedClaimNotes(notes ?? [])
      })
      .catch(() => {})
  }, [claim, dispatch, isVisible])

  const handleMemoViewNote = useCallback(
    (note: Note): void => {
      if (!claim) return
      const patientId = claim.patientIdSystem ?? claim.patientId
      if (!patientId) return
      const contextId = getNotesClaimContextId(claim)
      if (!contextId) return

      const claimTitleContext = getClaimIdForNotesHeader(claim) ?? 'Claim'
      const trimmedId = note.noteId?.trim()
      const openNoteId = trimmedId && trimmedId.length > 0 ? trimmedId : null
      const claimsOverlayBodyClass = 'note-overlay-from-claims'

      const matchedPatient: Patient | null =
        selectedPatient?.patient_id === patientId
          ? (selectedPatient as unknown as Patient)
          : null

      document.body.classList.add(claimsOverlayBodyClass)
      const didOpen = openNoteEditor({
        patient: matchedPatient,
        contextId,
        contextType: NoteContextType.CLAIMS,
        openNoteId,
        contextName: claimTitleContext,
        openFirstNote: true,
        initialView: 'list',
        hideNotesTable: true,
        overlayBackLabel: 'Back',
        editorBackButtonLabel: 'Back',
        openContextNotesByDefault: true,
        notesFetchLoadingContainer: 'body',
        portalTarget: '.page-content',
        anchorClassName: 'claims-note-editor-overlay-anchor',
        claimStatusForNotes: claim.generalStatus ?? claim.status,
        onNoteMutationSuccess: handleClaimMemoNoteMutationSuccess,
        onOverlayWillClose: (): void => {
          document.body.classList.remove(claimsOverlayBodyClass)
          claimMemoPatientFetchSessionRef.current += 1
          if (shouldRestoreMemoAfterNoteOverlayRef.current) {
            shouldRestoreMemoAfterNoteOverlayRef.current = false
            onRestoreAfterNoteOverlay?.()
          }
        },
      })

      if (!didOpen) {
        document.body.classList.remove(claimsOverlayBodyClass)
        return
      }

      shouldRestoreMemoAfterNoteOverlayRef.current = true
      closeModal()

      if (matchedPatient) return

      const fetchSession = ++claimMemoPatientFetchSessionRef.current

      void fetchPatientById(patientId)
        .then((response) => {
          if (fetchSession !== claimMemoPatientFetchSessionRef.current) return
          updateNoteEditor({
            patient: response.data.patient as unknown as Patient,
          })
        })
        .catch(() => {
          if (fetchSession !== claimMemoPatientFetchSessionRef.current) return
          closeNoteEditor()
        })
    },
    [
      claim,
      selectedPatient,
      closeModal,
      onRestoreAfterNoteOverlay,
      openNoteEditor,
      updateNoteEditor,
      closeNoteEditor,
      handleClaimMemoNoteMutationSuccess,
    ],
  )

  const shouldDelayMemoModalRender =
    isVisible &&
    claimNotesContextId != null &&
    (isFetchingClaimNotes || fetchedClaimNotes === null)

  if (shouldDelayMemoModalRender) {
    return null
  }

  return (
    <Modal
      show={isVisible}
      onHide={closeModal}
      dialogClassName="claim-memo-modal"
      backdrop="static"
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>Memo</Modal.Title>
      </Modal.Header>
      <Modal.Body className="claim-memo-modal-body">
        <Row className="mb-2">
          <Col>
            <ButtonGroup
              buttons={[
                { label: 'Notes', value: 'notes' },
                { label: 'Attachments', value: 'attachments' },
              ]}
              activeTab={activeTab}
              onButtonClick={setActiveTab}
            />
          </Col>
        </Row>

        {activeTab === 'notes' ? (
          (() => {
            const selectedAttachment = selectedAttachmentId
              ? claim?.attachments.find(
                  (att) => att.itemNo === selectedAttachmentId,
                )
              : undefined
            const attachmentNotes: Note[] = selectedAttachment?.notes ?? []
            const claimNotesSource: INote[] =
              fetchedClaimNotes !== null
                ? fetchedClaimNotes
                : (claim?.notes ?? [])
            const claimLevelNotes =
              claim != null
                ? mapClaimNotesToMemoNotes(
                    claimNotesSource.length > 0 ? claimNotesSource : undefined,
                    claim,
                    {
                      localUser: localStorageUser,
                      loggedInUser,
                    },
                  )
                : []
            const notesToShow: Note[] =
              attachmentNotes.length > 0 ? attachmentNotes : claimLevelNotes
            const hasNotes = notesToShow.length > 0 || !!selectedAttachmentId
            const canOpenClaimNotesOverlay = Boolean(
              claim &&
                getNotesClaimContextId(claim) &&
                (claim.patientIdSystem ?? claim.patientId),
            )

            return hasNotes ? (
              <ClaimMemoNote
                memoNotes={notesToShow}
                onUpdateNotes={
                  readOnly || !selectedAttachmentId
                    ? undefined
                    : handleUpdateNotes
                }
                readOnly={readOnly}
                onViewNote={
                  canOpenClaimNotesOverlay ? handleMemoViewNote : undefined
                }
              />
            ) : (
              <div>
                <ListGroup>
                  <ListGroup.Item className="text-muted">
                    No notes available.
                  </ListGroup.Item>
                </ListGroup>
              </div>
            )
          })()
        ) : (
          <ClaimMemoAttachments claim={claim!} />
        )}
      </Modal.Body>
    </Modal>
  )
}

export default ClaimMemoModal
